import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, CalendarIcon, Mail, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useHeaderData } from "@/hooks/use-header-data";

const Reports = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [reportType, setReportType] = useState<string>("expense");
  const [dateFrom, setDateFrom] = useState<Date>(new Date());
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { organization, organizations } = useHeaderData();

  console.log("🔍 DEBUG (Reports): Current organizationId:", organizationId);
  console.log("🔍 DEBUG (Reports): Is mobile:", isMobile);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
      } else {
        setUser(session.user);
      }
    };
    checkAuth();
  }, [navigate]);

  const { data: reports, isLoading, refetch } = useQuery({
    queryKey: ["reports", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from("reports")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!organizationId,
    refetchInterval: 5000,
  });

  const handleGenerateReport = async () => {
    if (!organizationId) return;

    try {
      // Create report
      const { data: report, error } = await supabase
        .from("reports")
        .insert({
          name: `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report - ${format(dateFrom, 'MMM yyyy')}`,
          type: reportType,
          organization_id: organizationId,
          date_from: format(dateFrom, 'yyyy-MM-dd'),
          date_to: format(dateTo, 'yyyy-MM-dd'),
          status: 'pending',
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Report generation started",
        description: "Your report is being generated. This may take a few moments.",
      });

      // Trigger report processing
      const response = await supabase.functions.invoke('process-report', {
        body: { id: report.id }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      // Refetch reports list
      refetch();
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEmailReport = async (reportId: string) => {
    if (!emailTo) {
      toast({
        title: "Error",
        description: "Please enter an email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await supabase.functions.invoke('process-report', {
        body: { 
          id: reportId,
          email_to: emailTo
        }
      });

      if (response.error) throw response.error;

      toast({
        title: "Success",
        description: "Report has been sent to the specified email.",
      });

      setEmailDialogOpen(false);
      setEmailTo("");
      setSelectedReportId(null);
      refetch();
    } catch (error) {
      console.error("Error emailing report:", error);
      toast({
        title: "Error",
        description: "Failed to send report. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (report_url: string, format: 'pdf' | 'csv') => {
    try {
      // Create a proxy request through Supabase Edge Function
      const response = await supabase.functions.invoke('process-report', {
        body: { 
          action: 'download',
          report_url,
          format
        }
      });

      if (response.error) throw new Error(response.error.message);

      // Convert the base64 data to a Blob
      const base64Data = response.data.fileData;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      
      // Create and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `report.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading report:", error);
      toast({
        title: "Error",
        description: "Failed to download report. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Generate a consistent color based on organization name
  const getOrganizationColor = (name: string) => {
    const colors = [
      'bg-[#9b87f5]',
      'bg-[#7E69AB]',
      'bg-[#0EA5E9]',
      'bg-[#1EAEDB]',
      'bg-[#6E59A5]',
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Render appropriate layout based on mobile or desktop
  return (
    <div className="flex min-h-screen bg-background flex-col">
      {/* Header */}
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - only shown on desktop */}
        {!isMobile && <Sidebar className="border-r border-border/50" />}
        
        {/* Main content */}
        <main className="flex-1 overflow-y-auto pb-20">
          <div className="container mx-auto px-4 py-6 space-y-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
              <p className="text-muted-foreground">
                Generate and manage your reports
              </p>
            </div>

            {isMobile ? (
              <div className="flex flex-col space-y-4">
                <Card className="ios-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">New Report</CardTitle>
                    <CardDescription>Create a custom report</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col space-y-4">
                      <div>
                        <Label htmlFor="report-type">Report Type</Label>
                        <Select
                          value={reportType}
                          onValueChange={setReportType}
                        >
                          <SelectTrigger className="w-full mt-1 ios-input">
                            <SelectValue placeholder="Select report type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="expense">Expense Report</SelectItem>
                            <SelectItem value="summary">Summary Report</SelectItem>
                            <SelectItem value="category">Category Report</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Date Range</Label>
                        <div className="flex space-x-2">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="ios-date-trigger flex-1 justify-start text-left"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(dateFrom, "MMM d, yyyy")}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 ios-date-picker-content" align="center">
                              <Calendar
                                mode="single"
                                selected={dateFrom}
                                onSelect={(date) => date && setDateFrom(date)}
                                initialFocus
                                className="ios-date-picker"
                              />
                            </PopoverContent>
                          </Popover>
                          
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="ios-date-trigger flex-1 justify-start text-left"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(dateTo, "MMM d, yyyy")}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 ios-date-picker-content" align="center">
                              <Calendar
                                mode="single"
                                selected={dateTo}
                                onSelect={(date) => date && setDateTo(date)}
                                initialFocus
                                className="ios-date-picker"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      
                      <Button onClick={handleGenerateReport} className="w-full ios-button">
                        <Plus className="h-4 w-4 mr-2" />
                        Generate Report
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="ios-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Your Reports</CardTitle>
                    <CardDescription>View and manage generated reports</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {isLoading ? (
                      <div className="flex justify-center py-8">
                        <LoadingSpinner />
                      </div>
                    ) : reports && reports.length > 0 ? (
                      <div className="divide-y">
                        {reports.map((report) => (
                          <div key={report.id} className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center">
                                <FileText className="h-4 w-4 text-muted-foreground mr-2" />
                                <span className="font-medium">{report.name}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                report.status === 'completed' 
                                  ? 'bg-green-100 text-green-800'
                                  : report.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {report.status}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground mb-2">
                              <span className="block">{format(new Date(report.date_from), 'MMM d, yyyy')} - {format(new Date(report.date_to), 'MMM d, yyyy')}</span>
                              <span className="block capitalize">Type: {report.type}</span>
                            </div>
                            {report.status === 'completed' && report.report_url && (
                              <div className="flex gap-2 mt-3">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="text-xs h-8">
                                      <Download className="h-3 w-3 mr-1" />
                                      Download
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleDownload(report.report_url!, 'pdf')}>
                                      Download as PDF
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownload(report.report_url!, 'csv')}>
                                      Download as CSV
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-8"
                                  onClick={() => {
                                    setSelectedReportId(report.id);
                                    setEmailDialogOpen(true);
                                  }}
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  Email
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground px-4">
                        No reports found. Generate your first report using the button above.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <CardDescription className="text-sm text-muted-foreground">
                    Track, submit and manage your business expenses
                  </CardDescription>
                  <div className="flex items-center gap-4">
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="ios-date-trigger w-[180px] justify-start text-left"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(dateFrom, "MMM d, yyyy")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 ios-date-picker-content" align="start">
                          <div className="ios-date-picker-header">
                            <h3 className="font-semibold">Select Start Date</h3>
                          </div>
                          <Calendar
                            mode="single"
                            selected={dateFrom}
                            onSelect={(date) => date && setDateFrom(date)}
                            initialFocus
                            className="ios-date-picker"
                          />
                          <div className="p-3 border-t flex justify-end">
                            <button 
                              className="ios-date-picker-button"
                              onClick={() => document.body.click()}
                            >
                              Done
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="ios-date-trigger w-[180px] justify-start text-left"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(dateTo, "MMM d, yyyy")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 ios-date-picker-content" align="start">
                          <div className="ios-date-picker-header">
                            <h3 className="font-semibold">Select End Date</h3>
                          </div>
                          <Calendar
                            mode="single"
                            selected={dateTo}
                            onSelect={(date) => date && setDateTo(date)}
                            initialFocus
                            className="ios-date-picker"
                          />
                          <div className="p-3 border-t flex justify-end">
                            <button 
                              className="ios-date-picker-button"
                              onClick={() => document.body.click()}
                            >
                              Done
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Select
                      value={reportType}
                      onValueChange={setReportType}
                    >
                      <SelectTrigger className="w-[180px] ios-input">
                        <SelectValue placeholder="Select report type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expense">Expense Report</SelectItem>
                        <SelectItem value="summary">Summary Report</SelectItem>
                        <SelectItem value="category">Category Report</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleGenerateReport} className="ios-button">
                      Generate Report
                    </Button>
                  </div>
                </div>

                <Card className="p-6 ios-card">
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : reports && reports.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Date Range</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created At</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.map((report) => (
                          <TableRow key={report.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                {report.name}
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">{report.type}</TableCell>
                            <TableCell>
                              {format(new Date(report.date_from), 'MMM d, yyyy')} - {format(new Date(report.date_to), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                report.status === 'completed' 
                                  ? 'bg-green-100 text-green-800'
                                  : report.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {report.status}
                              </span>
                            </TableCell>
                            <TableCell>{format(new Date(report.created_at || ''), 'MMM d, yyyy')}</TableCell>
                            <TableCell className="text-right">
                              {report.status === 'completed' && report.report_url && (
                                <div className="flex justify-end gap-2">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" size="sm">
                                        <Download className="h-4 w-4 mr-2" />
                                        Download
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                      <DropdownMenuItem onClick={() => handleDownload(report.report_url!, 'pdf')}>
                                        Download as PDF
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDownload(report.report_url!, 'csv')}>
                                        Download as CSV
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedReportId(report.id);
                                      setEmailDialogOpen(true);
                                    }}
                                  >
                                    <Mail className="h-4 w-4 mr-2" />
                                    Email
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No reports found. Generate your first report using the button above.
                    </div>
                  )}
                </Card>
              </>
            )}
          </div>
        </main>
      </div>

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="ios-dialog">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Email Report</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter recipient's email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="ios-input"
              />
            </div>
            <Button onClick={() => selectedReportId && handleEmailReport(selectedReportId)} className="ios-button">
              Send Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
